import React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import CreateClient from './client_list/ClientForm'

import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import VersatileGrid from '../../ui_reusable/VersatileGrid'
import { SmallStandardButton } from '../../ui_reusable/styles/Button.styles'
import {
  GridCellBoldText,
  GridCellSmallFadedText,
  ListItemInVersatileGrid,
} from '../../ui_reusable/styles/VersatileGridUtils.styles'
import { ExpansiveListInVersatileGrid } from '../../ui_reusable/VersatileGridUtils'
import { ProjectTag } from './styles/ClientList.styles'
import { StatsContainer } from '../../ui_reusable/styles/VersatileGridUtils.styles'
import { TagSuccess, TagWarning } from '../../ui_reusable/Tags'
import { SmallNormalTag } from '../../ui_reusable/styles/Tags.styles'
import { ThickSelectPicker } from '../../ui_reusable/StandardSelectPickers'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
/* Layout components for header organization */
import { HeaderMedium600Style } from '../../../styles/Text.styles'
import { useClientContext } from '../../../context/ClientContext'

const PAGE_SIZE = 8

const ClientListPage = () => {
  const navigate = useNavigate()

  const [clients, setClients] = useState([])
  const [total, setTotal] = useState(0)
  const [active, setActive] = useState(0)
  const [inactive, setInactive] = useState(0)
  const [page, setPage] = useState(1) // 1-based, matches `?page=` API contract
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(null) // { key (backend field), direction } | null
  const [clientOptions, setClientOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [refresh, setRefresh] = useState(new Date().getTime())
  const { setSelectedClient } = useClientContext()

  useEffect(() => {
    const abortController = new AbortController()

    const getClients = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          page: String(page),
        })
        if (search) {
          params.set('search', search)
        }
        if (sort) {
          params.set(
            'ordering',
            `${sort.direction === 'desc' ? '-' : ''}${sort.key}`,
          )
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/clients/?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            signal: abortController.signal,
          },
        )

        // DRF 404s on out-of-range pages (e.g. last item on the page was
        // deleted) — step back to the previous page and let the effect refire.
        if (response.status === 404 && page > 1) {
          setPage(page - 1)
          return
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const res = await response.json()

        setClients(res.data || [])
        setTotal(res.total ?? res.data?.length ?? 0)
        setActive(res.meta?.active ?? 0)
        setInactive(res.meta?.inactive ?? 0)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch clients:', error)
          setClients([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    getClients()

    return () => {
      abortController.abort()
    }
  }, [refresh, page, search, sort])

  // Lite unpaginated list for the header picker
  useEffect(() => {
    const abortController = new AbortController()

    const getClientOptions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/clients/?ordering=name`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            signal: abortController.signal,
          },
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const res = await response.json()

        setClientOptions(res.data || [])
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch client options:', error)
          setClientOptions([])
        }
      }
    }

    getClientOptions()

    return () => {
      abortController.abort()
    }
  }, [refresh])

  const handleManage = useCallback(
    async (id) => {
      let client =
        clientOptions.find((elem) => elem.id === id) ||
        clients.find((elem) => elem.id === id)

      if (!client) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/clients/${id}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
            },
          )

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const res = await response.json()

          client = res.data
        } catch (error) {
          console.error('Failed to fetch client:', error)
          return
        }
      }

      if (!client) return

      setSelectedClient({ id: client.id, name: client.name, logo: client.logo })
      navigate('/clients/' + id)
    },
    [clientOptions, clients, navigate, setSelectedClient],
  )

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handleSearchChange = useCallback((term) => {
    setPage(1)
    setSearch(term)
  }, [])

  const handleSortChange = useCallback((nextSort) => {
    setPage(1)
    setSort(nextSort)
  }, [])

  // Creating a client lands it on page 1 (backend default `ordering=-id`),
  // so refresh also jumps back to the first page.
  const handleRefresh = useCallback(() => {
    setPage(1)
    setRefresh(new Date().getTime())
  }, [])

  const gridHeader = (
    <div>
      <StatsContainer>
        <span css={HeaderMedium600Style}>Total Clients: {total}</span>
        <div>
          <SmallNormalTag>{active} active</SmallNormalTag>
          <SmallNormalTag>{inactive} inactive</SmallNormalTag>
        </div>
      </StatsContainer>
    </div>
  )

  // Function to flatten client data for search functionality
  const getFlattenedClientData = () => {
    return clients.map((client) => ({
      ...client,
      contact: client.contact?.name || '',
      kam: client.kam?.name || '',
      // Don't convert projects to string, create searchable field instead
      projectsSearchable: client.projects?.map((p) => p.name).join(', ') || '',
    }))
  }

  const headers = [
    {
      title: 'Client name',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      serverKey: 'name',
    },
    {
      title: 'Contact',
      key: 'contact',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'KAM',
      key: 'kam',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'Created',
      key: 'created_at',
      type: 'datetime',
      isSortable: true,
      isSearchable: false,
      serverKey: 'created_at',
    },
    {
      title: 'Projects',
      key: 'projectsSearchable', // Change the key to the searchable field
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Status',
      key: 'is_active',
      type: 'boolean',
      isSortable: false,
      isSearchable: false,
      valueMapping: {
        // New property instead of searchRankingRule
        true: 'active',
        false: 'inactive',
      },
    },
    {
      title: '',
      key: 'actions',
      type: 'none',
      isSortable: false,
      isSearchable: false,
    },
  ]

  /* Row rendering configuration with all position parameters
   * @param rowData - The data for this row
   * @param rowIdx - Current visual position in grid (accounts for intersecting rows)
   * @param dataIdx - Original position in data array
   */
  const renderRow = (rowData, rowIdx, dataIdx) => [
    <GridCellBoldText>{rowData.name}</GridCellBoldText>,
    <GridCellBoldText>
      {rowData?.contact?.name || rowData.contact}
    </GridCellBoldText>,
    <GridCellSmallFadedText>
      {rowData?.kam?.name || rowData.kam}
    </GridCellSmallFadedText>,
    <GridCellSmallFadedText>
      {new Date(rowData.created_at).toISOString().slice(0, 10)}
    </GridCellSmallFadedText>,
    <GridCellSmallFadedText>
      <ExpansiveListInVersatileGrid initialVisibleItems={2}>
        {Array.isArray(rowData.projects) &&
          rowData.projects.map((project, index) => (
            <ListItemInVersatileGrid key={index}>
              <ProjectTag key={index}>{project.name}</ProjectTag>
            </ListItemInVersatileGrid>
          ))}
      </ExpansiveListInVersatileGrid>
    </GridCellSmallFadedText>,
    <div>
      {rowData.is_active ? (
        <TagSuccess label="Active" />
      ) : (
        <TagWarning label="Inactive" />
      )}
    </div>,
    <SmallStandardButton
      onClick={() => handleManage(rowData.id)}
      data-testid={`manage-${rowData.name}`}
    >
      Manage
    </SmallStandardButton>,
  ]

  return (
    <>
      {/* Page Header Section */}
      <HeaderContainer>
        <SectionTitle>Manage Clients</SectionTitle>

        <ThickSelectPicker
          placeholder="Select client"
          defaultValue={0}
          size="lg"
          data={clientOptions.map((client, i) => ({
            key: i + 1,
            label: client.name,
            value: client.id,
          }))}
          onChange={handleManage}
          style={{ width: 250 }}
        />
      </HeaderContainer>

      {/* Main Content Section */}
      <SimpleAccordion id={'clientPageAccordion'} defaultActiveKey={[1]}>
        <SimpleAccordion.Panel eventKey={1} noCollapse>
          {/* Client Creation Interface
           */}
          <CreateClient setRefresh={handleRefresh} />

          {/* Client Data Grid */}
          <VersatileGrid
            headers={headers}
            data={getFlattenedClientData()}
            renderRow={renderRow}
            maxDisplayedItems={PAGE_SIZE}
            serverSide
            serverTotal={total}
            serverPage={page}
            onPageChange={handlePageChange}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            loading={loading}
            headerInfo={gridHeader}
            loadingMessage={'Loading clients...'}
          />
        </SimpleAccordion.Panel>
      </SimpleAccordion>
    </>
  )
}

export default ClientListPage
